import { useMemo, useState, type ReactNode } from 'react'
import {
  Delete,
  Eraser,
  ChevronDown,
  AlertTriangle,
  Hash,
  Quote,
  GitBranch,
  Sigma,
  SquareRadical,
  CalendarClock,
  CalendarDays,
  Undo2,
  Redo2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import {
  OPERATORS,
  COMPARISON_OPERATORS,
  evaluateFormula,
  formatCalculated,
  formulaToHuman,
  splitFormulaTokens,
  toStringLiteral,
  type DisplayFormat,
} from '@/lib/formula'
import { ConditionBuilder } from './ConditionBuilder'
import { AggregateBuilder } from './AggregateBuilder'
import { DateFunctionBuilder } from './DateFunctionBuilder'
import { MathFunctionBuilder } from './MathFunctionBuilder'
import { FormulaTokenStrip } from './FormulaTokenStrip'
import { FormulaTokenChip } from './FormulaTokenChip'
import { useFormulaChipEditor } from './useFormulaChipEditor'
import { findBrokenRefs } from './formulaRefPicker'
import type { CrossFormAuthoring } from '@/hooks/useCrossFormAuthoring'

/** A reference offered by the structured IF / SUM / MAX / date / math builders. */
export interface FormulaOperand {
  id: string
  label: string
}

const DISPLAY_FORMAT_LABELS: Record<DisplayFormat, string> = {
  decimal3: 'Decimal (0.000)',
  decimal2: 'Decimal (0.00)',
  number: 'Whole number',
  date: 'Date (Aug 15, 2026)',
  datetime: 'Date & time (Aug 15, 2026 2:30 PM)',
}

const OP_SYMBOL: Record<string, string> = {
  '+': '+', '-': '−', '*': '×', '/': '÷', '(': '(', ')': ')',
  '==': '==', '!=': '≠', '<': '<', '<=': '≤', '>': '>', '>=': '≥',
  ',': ',', 'IF': 'IF', 'AND': 'AND', 'OR': 'OR', 'ANY': 'ANY', 'ALL': 'ALL',
  'SUM': 'SUM', 'MAX': 'MAX',
  'LOG10': 'LOG10', 'ABS': 'ABS', 'CEILING': 'CEILING', 'FLOOR': 'FLOOR', 'ROUND': 'ROUND',
}

interface Props {
  formula: string
  onFormulaChange: (formula: string) => void
  displayFormat: DisplayFormat | undefined
  /** What the format select shows while nothing is stored. */
  defaultDisplayFormat: DisplayFormat
  onDisplayFormatChange: (format: DisplayFormat) => void
  /** Label for every reference that resolves; a ref missing here is broken. */
  labels: Record<string, string>
  /** Cross-form refs ({handle.fieldId}) get their own chip colour. */
  isCrossRef: (id: string) => boolean
  /** Whether the live preview should take a number for this ref. */
  isNumericRef: (id: string) => boolean
  /** Operands for SUM/MAX, DATEADD/DATEDIFF and the math functions. */
  operands: FormulaOperand[]
  /** Operands for the IF condition builder. */
  conditionOperands: FormulaOperand[]
  /** Replace-editor for a reference chip. */
  renderRefEditor: (id: string, replace: (token: string) => void) => ReactNode
  /** The host's "add a reference" section; `insert` takes a `{ref}` token. */
  renderReferencePicker: (insert: (token: string) => void) => ReactNode
  /** Rendered above the formula (a heading, quick totals, …). */
  header?: ReactNode
  /** Host-specific warnings, under the broken-reference one. */
  extraWarnings?: ReactNode
  /** Forces the formula strip's error outline (e.g. for a host warning). */
  hasExtraError?: boolean
  emptyHint: string
  /** Chip text for a reference that no longer resolves. */
  brokenLabel: string
  /** Noun for a broken reference in the warning, singular and plural. */
  refNoun: [string, string]
}

/** Notes under a reference picker about where earlier forms' fields come from. */
export function CrossFormHints({
  crossForm,
  hasUpstream,
}: {
  crossForm?: CrossFormAuthoring
  hasUpstream: boolean
}) {
  return (
    <>
      {crossForm?.available && crossForm.procedureTemplates.length > 1 && (
        <p className='text-[11px] text-muted-foreground'>
          Earlier forms come from the template selected in the toolbar (
          {crossForm.selectedProcedureTemplateId
            ? crossForm.procedureTemplates.find(p => p.id === crossForm.selectedProcedureTemplateId)?.name
            : 'None'}
          ).
        </p>
      )}
      {hasUpstream && (
        <p className='text-[11px] text-violet-700/80'>
          Fields from earlier forms read the patient's own submission at fill time —
          empty until that form is filled.
        </p>
      )}
    </>
  )
}

/**
 * The formula editor shared by ordinary calculated fields (FormulaBuilder) and
 * calculated table cells (CellFormulaBuilder), so both offer exactly the same
 * functions and tools. Only what differs between the two is passed in: which
 * references exist, how to pick one, and how each is labelled.
 */
export function FormulaEditor({
  formula,
  onFormulaChange,
  displayFormat,
  defaultDisplayFormat,
  onDisplayFormatChange,
  labels,
  isCrossRef,
  isNumericRef,
  operands,
  conditionOperands,
  renderRefEditor,
  renderReferencePicker,
  header,
  extraWarnings,
  hasExtraError,
  emptyHint,
  brokenLabel,
  refNoun,
}: Props) {
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [literal, setLiteral] = useState('')
  const [sampleValues, setSampleValues] = useState<Record<string, string>>({})

  const editor = useFormulaChipEditor(formula, onFormulaChange)
  const tokens = editor.tokens

  const known = (id: string) => labels[id] !== undefined
  const brokenRefs = useMemo(() => findBrokenRefs(formula, id => labels[id] !== undefined), [formula, labels])
  const referencedIds = useMemo(
    () => [...new Set([...formula.matchAll(/\{([^}]+)\}/g)].map(m => m[1]))].filter(id => labels[id] !== undefined),
    [formula, labels],
  )

  const addOperator = (op: string) => editor.insert([op])

  // Structured IF editor — builds a complete IF(cond, then, else) expression
  // and appends it in one piece, so no half-filled scaffold ever hits the formula.
  const [ifOpen, setIfOpen] = useState(false)
  // Structured SUM/MAX editor — same one-piece-insert approach as IF.
  const [aggOpen, setAggOpen] = useState<'SUM' | 'MAX' | null>(null)
  // Structured DATEADD/DATEDIFF editor — same one-piece-insert approach.
  const [dateFnOpen, setDateFnOpen] = useState<'DATEADD' | 'DATEDIFF' | null>(null)
  // Structured LOG10/ABS/CEILING/FLOOR/ROUND editor — one panel for all five,
  // so the operator row keeps a single button instead of gaining five.
  const [mathFnOpen, setMathFnOpen] = useState(false)

  const addToday = () => editor.insert(['TODAY', '(', ')'])
  const addNow = () => editor.insert(['NOW', '(', ')'])

  const literalIsNumber = literal.trim() !== '' && Number.isFinite(Number(literal.trim()))
  const addNumber = () => {
    if (!literalIsNumber) return
    editor.insert([literal.trim()])
    setLiteral('')
  }
  const addText = () => {
    if (literal === '') return
    editor.insert([toStringLiteral(literal)])
    setLiteral('')
  }

  // Live preview using the admin's sample values.
  const preview = useMemo(
    () => formatCalculated(evaluateFormula(formula, sampleValues), displayFormat),
    [formula, sampleValues, displayFormat],
  )

  const human = formula ? formulaToHuman(formula, labels) : ''

  return (
    <div className='space-y-3'>
      {header}

      {/* Visual formula row — click a gap to place the cursor, click a chip to
          replace or remove it in place. */}
      <FormulaTokenStrip
        tokens={tokens}
        caret={editor.caret}
        onCaretClick={editor.setCaret}
        hasError={brokenRefs.length > 0 || !!hasExtraError}
        emptyHint={emptyHint}
        renderToken={(tok, i) => {
          const refMatch = tok.match(/^\{(.+)\}$/)
          const chipProps = {
            token: tok,
            isSelected: editor.selectedIndex === i,
            onOpenChange: (open: boolean) => editor.setSelectedIndex(open ? i : null),
            onReplace: (newToken: string) => editor.replace(i, newToken),
            onRemove: () => editor.removeAt(i),
          }
          if (refMatch) {
            const id = refMatch[1]
            return (
              <FormulaTokenChip
                {...chipProps}
                className={
                  known(id)
                    ? isCrossRef(id)
                      ? 'bg-violet-100 text-violet-700'
                      : 'bg-primary/10 text-primary'
                    : 'bg-destructive/10 text-destructive'
                }
                refEditor={replace => renderRefEditor(id, replace)}
              >
                {known(id) ? labels[id] : brokenLabel}
              </FormulaTokenChip>
            )
          }
          if (tok.startsWith('"')) {
            return (
              <FormulaTokenChip {...chipProps} className='bg-emerald-100 text-emerald-700'>
                {tok}
              </FormulaTokenChip>
            )
          }
          if (/^\d/.test(tok)) {
            return (
              <FormulaTokenChip {...chipProps} className='bg-amber-100 text-amber-700'>
                {tok}
              </FormulaTokenChip>
            )
          }
          return (
            <FormulaTokenChip {...chipProps} className='font-semibold text-muted-foreground'>
              {OP_SYMBOL[tok] ?? tok}
            </FormulaTokenChip>
          )
        }}
      />

      {/* Primary operator row + IF button. Wraps rather than overflowing: with
          IF/SUM/MAX alongside the 6 operators and the edit actions, this no
          longer fits one line in the properties panel when zoomed or on mobile. */}
      <div className='flex flex-wrap items-center gap-1'>
        {OPERATORS.map(op => (
          <Button
            key={op.token}
            type='button'
            size='icon'
            variant='outline'
            className='h-7 w-7 shrink-0 text-sm'
            title={op.label}
            onClick={() => addOperator(op.token)}
          >
            {op.symbol}
          </Button>
        ))}
        <Button
          type='button'
          size='sm'
          variant={ifOpen ? 'secondary' : 'outline'}
          className='h-7 shrink-0 gap-1 px-2 text-xs'
          title='Build an IF(condition, then, else) expression'
          onClick={() => setIfOpen(o => !o)}
        >
          <GitBranch className='h-3 w-3' />
          IF
        </Button>
        <Button
          type='button'
          size='sm'
          variant={aggOpen === 'SUM' ? 'secondary' : 'outline'}
          className='h-7 shrink-0 gap-1 px-2 text-xs'
          title='Build a SUM(e1, e2, …) expression'
          onClick={() => setAggOpen(o => (o === 'SUM' ? null : 'SUM'))}
        >
          <Sigma className='h-3 w-3' />
          SUM
        </Button>
        <Button
          type='button'
          size='sm'
          variant={aggOpen === 'MAX' ? 'secondary' : 'outline'}
          className='h-7 shrink-0 gap-1 px-2 text-xs'
          title='Build a MAX(e1, e2, …) expression'
          onClick={() => setAggOpen(o => (o === 'MAX' ? null : 'MAX'))}
        >
          <Sigma className='h-3 w-3' />
          MAX
        </Button>
        <Button
          type='button'
          size='sm'
          variant={mathFnOpen ? 'secondary' : 'outline'}
          className='h-7 shrink-0 gap-1 px-2 text-xs'
          title='Build a LOG10 / ABS / CEILING / FLOOR / ROUND expression'
          onClick={() => setMathFnOpen(o => !o)}
        >
          <SquareRadical className='h-3 w-3' />
          MATH
        </Button>
        <Button
          type='button'
          size='sm'
          variant='outline'
          className='h-7 shrink-0 gap-1 px-2 text-xs'
          title="Insert today's date"
          onClick={addToday}
        >
          <CalendarDays className='h-3 w-3' />
          TODAY
        </Button>
        <Button
          type='button'
          size='sm'
          variant='outline'
          className='h-7 shrink-0 gap-1 px-2 text-xs'
          title='Insert the current date and time'
          onClick={addNow}
        >
          <CalendarDays className='h-3 w-3' />
          NOW
        </Button>
        <Button
          type='button'
          size='sm'
          variant={dateFnOpen === 'DATEADD' ? 'secondary' : 'outline'}
          className='h-7 shrink-0 gap-1 px-2 text-xs'
          title='Build a DATEADD(date, amount, unit) expression'
          onClick={() => setDateFnOpen(o => (o === 'DATEADD' ? null : 'DATEADD'))}
        >
          <CalendarClock className='h-3 w-3' />
          DATEADD
        </Button>
        <Button
          type='button'
          size='sm'
          variant={dateFnOpen === 'DATEDIFF' ? 'secondary' : 'outline'}
          className='h-7 shrink-0 gap-1 px-2 text-xs'
          title='Build a DATEDIFF(start, end, unit) expression'
          onClick={() => setDateFnOpen(o => (o === 'DATEDIFF' ? null : 'DATEDIFF'))}
        >
          <CalendarClock className='h-3 w-3' />
          DATEDIFF
        </Button>
        {/* ml-auto keeps these right-aligned on one line, and keeps them
            together as a unit once the row wraps. */}
        <div className='ml-auto flex shrink-0 items-center gap-1'>
          <Button
            type='button'
            size='icon'
            variant='ghost'
            className='h-7 w-7 text-muted-foreground'
            title='Undo'
            onClick={editor.undo}
            disabled={!editor.canUndo}
          >
            <Undo2 className='h-3.5 w-3.5' />
          </Button>
          <Button
            type='button'
            size='icon'
            variant='ghost'
            className='h-7 w-7 text-muted-foreground'
            title='Redo'
            onClick={editor.redo}
            disabled={!editor.canRedo}
          >
            <Redo2 className='h-3.5 w-3.5' />
          </Button>
          <Button
            type='button'
            size='icon'
            variant='ghost'
            className='h-7 w-7 text-muted-foreground'
            title='Remove last'
            onClick={editor.backspace}
            disabled={tokens.length === 0}
          >
            <Delete className='h-3.5 w-3.5' />
          </Button>
          <Button
            type='button'
            size='icon'
            variant='ghost'
            className='h-7 w-7 text-muted-foreground'
            title='Clear formula'
            onClick={editor.clear}
            disabled={tokens.length === 0}
          >
            <Eraser className='h-3.5 w-3.5' />
          </Button>
        </div>
      </div>

      {/* Comparison operator row (for use inside IF conditions) */}
      <div className='flex flex-wrap items-center gap-1'>
        <span className='mr-1 shrink-0 text-[11px] text-muted-foreground'>Compare:</span>
        {COMPARISON_OPERATORS.map(op => (
          <Button
            key={op.token}
            type='button'
            size='icon'
            variant='outline'
            className='h-6 w-7 shrink-0 text-xs'
            title={op.label}
            onClick={() => addOperator(op.token)}
          >
            {op.symbol}
          </Button>
        ))}
        <Button
          type='button'
          size='sm'
          variant='outline'
          className='h-6 shrink-0 px-2 text-xs'
          title='Logical AND — both conditions must be true'
          onClick={() => addOperator('AND')}
        >
          AND
        </Button>
        <Button
          type='button'
          size='sm'
          variant='outline'
          className='h-6 shrink-0 px-2 text-xs'
          title='Logical OR — either condition must be true'
          onClick={() => addOperator('OR')}
        >
          OR
        </Button>
      </div>

      {ifOpen && (
        <ConditionBuilder
          operands={conditionOperands}
          onInsert={f => {
            editor.insert(splitFormulaTokens(f))
            setIfOpen(false)
          }}
          onCancel={() => setIfOpen(false)}
        />
      )}

      {aggOpen && (
        <AggregateBuilder
          fn={aggOpen}
          operands={operands}
          onInsert={f => {
            editor.insert(splitFormulaTokens(f))
            setAggOpen(null)
          }}
          onCancel={() => setAggOpen(null)}
        />
      )}

      {dateFnOpen && (
        <DateFunctionBuilder
          fn={dateFnOpen}
          operands={operands}
          onInsert={f => {
            editor.insert(splitFormulaTokens(f))
            setDateFnOpen(null)
          }}
          onCancel={() => setDateFnOpen(null)}
        />
      )}

      {mathFnOpen && (
        <MathFunctionBuilder
          operands={operands}
          onInsert={f => {
            editor.insert(splitFormulaTokens(f))
            setMathFnOpen(false)
          }}
          onCancel={() => setMathFnOpen(false)}
        />
      )}

      {/* Add a literal value (number or text) */}
      <div className='space-y-1.5'>
        <p className='text-xs text-muted-foreground'>Add a fixed value:</p>
        <div className='flex items-center gap-1.5'>
          <Input
            value={literal}
            onChange={e => setLiteral(e.target.value)}
            placeholder='e.g. 100 or " mL"'
            className='h-8 text-sm'
            onKeyDown={e => {
              if (e.key !== 'Enter') return
              e.preventDefault()
              if (literalIsNumber) addNumber()
              else addText()
            }}
          />
          <Button
            type='button'
            size='sm'
            variant='outline'
            className='h-8 shrink-0 px-2'
            title='Insert as a number'
            disabled={!literalIsNumber}
            onClick={addNumber}
          >
            <Hash className='mr-1 h-3.5 w-3.5' />
            Number
          </Button>
          <Button
            type='button'
            size='sm'
            variant='outline'
            className='h-8 shrink-0 px-2'
            title='Insert as text (for joining with +)'
            disabled={literal === ''}
            onClick={addText}
          >
            <Quote className='mr-1 h-3.5 w-3.5' />
            Text
          </Button>
        </div>
        <p className='text-[11px] text-muted-foreground'>
          Use <span className='font-semibold'>+</span> with text to join values, e.g. a
          result like <span className='font-mono'>"12.5 mL"</span>.
        </p>
      </div>

      {renderReferencePicker(token => editor.insert([token]))}

      {brokenRefs.length > 0 && (
        <div className='flex items-start gap-1.5 rounded-md border border-destructive/40 bg-destructive/5 px-2.5 py-2 text-xs text-destructive'>
          <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
          <span>
            This formula references {brokenRefs.length}{' '}
            {brokenRefs.length > 1 ? refNoun[1] : refNoun[0]} that no longer exist
            {brokenRefs.length > 1 ? '' : 's'}. Remove the highlighted token
            {brokenRefs.length > 1 ? 's' : ''} above.
          </span>
        </div>
      )}

      {extraWarnings}

      {/* Human-readable summary */}
      {human && (
        <div className='rounded-md bg-muted/40 px-2.5 py-2'>
          <p className='text-[11px] uppercase tracking-wide text-muted-foreground'>Summary</p>
          <p className='text-sm font-medium break-words'>{human}</p>
        </div>
      )}

      {/* Display format */}
      <div className='space-y-1.5'>
        <Label>Result format</Label>
        <Select
          value={displayFormat ?? defaultDisplayFormat}
          onValueChange={v => onDisplayFormatChange(v as DisplayFormat)}
        >
          <SelectTrigger className='h-8 text-sm'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(DISPLAY_FORMAT_LABELS) as DisplayFormat[]).map(fmt => (
              <SelectItem key={fmt} value={fmt}>
                {DISPLAY_FORMAT_LABELS[fmt]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className='text-[11px] text-muted-foreground'>
          Numeric formats apply to number results; Date / Date &amp; time format a
          DATEADD()/date-ref result. A non-matching result is shown as-is.
        </p>
      </div>

      {/* Live preview with sample values */}
      {referencedIds.length > 0 && (
        <div className='space-y-2 rounded-md border bg-card px-2.5 py-2.5'>
          <p className='text-[11px] uppercase tracking-wide text-muted-foreground'>
            Live preview
          </p>
          {referencedIds.map(id => {
            const numeric = isNumericRef(id)
            return (
              <div key={id} className='flex items-center gap-2'>
                <Label className='flex-1 truncate text-xs font-normal text-muted-foreground'>
                  {labels[id]}
                </Label>
                <Input
                  type={numeric ? 'number' : 'text'}
                  value={sampleValues[id] ?? ''}
                  placeholder={numeric ? '0' : 'text'}
                  className='h-7 w-24 text-sm'
                  onChange={e =>
                    setSampleValues(prev => ({ ...prev, [id]: e.target.value }))
                  }
                />
              </div>
            )
          })}
          <div className='flex items-center justify-between border-t pt-2'>
            <span className='text-xs text-muted-foreground'>Result</span>
            <span className='font-mono text-sm font-semibold'>= {preview}</span>
          </div>
        </div>
      )}

      {/* Advanced: raw formula */}
      <div>
        <button
          type='button'
          onClick={() => setShowAdvanced(s => !s)}
          className='flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground'
        >
          <ChevronDown
            className={cn('h-3.5 w-3.5 transition-transform', showAdvanced && 'rotate-180')}
          />
          Formula (advanced)
        </button>
        {showAdvanced && (
          <div className='mt-1.5 space-y-1'>
            <pre className='overflow-x-auto rounded bg-muted px-2.5 py-2 text-[11px] font-mono text-muted-foreground'>
              {formula || '(empty)'}
            </pre>
            <p className='text-[11px] text-muted-foreground'>
              The raw expression that is saved. References use stable IDs (the labels above
              are shown for readability); this is what the server stores and evaluates.
            </p>
            <p className='text-[11px] text-muted-foreground'>
              Conditional syntax:{' '}
              <span className='font-mono'>IF(condition, then_value, else_value)</span> — e.g.{' '}
              <span className='font-mono'>IF(&#123;weight&#125; &gt; 0, &#123;dose&#125; / &#123;weight&#125;, 0)</span>
            </p>
            <p className='text-[11px] text-muted-foreground'>
              Logical functions: <span className='font-mono'>ALL(c1, c2, …)</span> (every
              condition true) and <span className='font-mono'>ANY(c1, c2, …)</span> (at least
              one true) — usable anywhere a condition goes.
            </p>
            <p className='text-[11px] text-muted-foreground'>
              Aggregate functions: <span className='font-mono'>SUM(e1, e2, …)</span> (sum of
              the values, blanks skipped) and <span className='font-mono'>MAX(e1, e2, …)</span>{' '}
              (the largest value) — insert with the SUM / MAX buttons above.
            </p>
            <p className='text-[11px] text-muted-foreground'>
              Math functions: <span className='font-mono'>LOG10(x)</span> (base-10 logarithm —
              blank when x is zero or negative), <span className='font-mono'>ABS(x)</span>,{' '}
              <span className='font-mono'>CEILING(x)</span> / <span className='font-mono'>FLOOR(x)</span>{' '}
              (round to a whole number, up / down), and{' '}
              <span className='font-mono'>ROUND(x, decimals)</span> (round mid-formula, halves away
              from zero) — insert with the MATH button above. e.g.{' '}
              <span className='font-mono'>LOG10(&#123;pre_tcells&#125; / &#123;post_tcells&#125;)</span>
            </p>
            <p className='text-[11px] text-muted-foreground'>
              Date functions: <span className='font-mono'>TODAY()</span> /{' '}
              <span className='font-mono'>NOW()</span> (today's date, or the current date and
              time), <span className='font-mono'>DATEADD(date, amount, unit)</span> (add or
              subtract days/hours/minutes/weeks/months/years), and{' '}
              <span className='font-mono'>DATEDIFF(start, end, unit)</span> (the difference
              between two dates, in the given unit) — insert with the buttons above. Date and
              Date &amp; Time fields also support <span className='font-mono'>&lt;</span>{' '}
              <span className='font-mono'>&lt;=</span> <span className='font-mono'>&gt;</span>{' '}
              <span className='font-mono'>&gt;=</span> via the Compare row.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
